import { useCallback, useEffect, useState } from 'react';
import { listMicrophones, MicrophoneOption, requestMicrophoneAccess } from '../services/microphoneAccess';

export type MicrophoneState = 'checking' | 'ready' | 'denied';

export interface MicrophoneChoice {
  microphoneState: MicrophoneState;
  microphones: MicrophoneOption[];
  /** The picked microphone; '' is the browser's default. */
  selectedDeviceId: string;
  selectMicrophone: (deviceId: string) => void;
  /** List the microphones again (one plugged in since). */
  refresh: () => void;
}

/**
 * The co-seller's microphone choice in the join step. While `isEnabled` (a
 * desktop call, which can be co-sold) it asks for the microphone as the step
 * opens, so the browser's permission prompt comes before the user commits and
 * the picker can show device names (hidden until access is granted). A bot
 * call has no co-seller mode, so its step never asks.
 */
export function useMicrophoneChoice(isEnabled: boolean): MicrophoneChoice {
  const [microphoneState, setMicrophoneState] = useState<MicrophoneState>('checking');
  const [microphones, setMicrophones] = useState<MicrophoneOption[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const refreshMicrophones = useCallback(async () => {
    const available = await listMicrophones();
    setMicrophones(available);
    setSelectedDeviceId((current) =>
      available.some((microphone) => microphone.deviceId === current) ? current : available[0]?.deviceId ?? ''
    );
  }, []);

  useEffect(() => {
    if (!isEnabled) return undefined;
    let isMounted = true;
    const checkMicrophone = async () => {
      try {
        await requestMicrophoneAccess();
        if (!isMounted) return;
        await refreshMicrophones();
        if (isMounted) setMicrophoneState('ready');
      } catch (error) {
        console.warn('[useMicrophoneChoice] No microphone for a co-seller join:', error);
        if (isMounted) setMicrophoneState('denied');
      }
    };
    checkMicrophone();
    return () => {
      isMounted = false;
    };
  }, [isEnabled, refreshMicrophones]);

  const refresh = useCallback(() => {
    refreshMicrophones().catch((error) => console.warn('[useMicrophoneChoice] Could not list microphones:', error));
  }, [refreshMicrophones]);

  return { microphoneState, microphones, selectedDeviceId, selectMicrophone: setSelectedDeviceId, refresh };
}
